<template>
  <content-container :overlay="false">
    <!-- User Name ------------------------------------------------------------->
    <collapsible-card
        ref="name"
        :loading="loading || saveNameRequest"
        :changed="isUserDataChanged()"
        no-close-on-ok
        @click="handleSaveUserName()"
        @close="setUserFields()"
    >
      <template v-slot:title>
        {{ $t('profile.account.name.title') }}
      </template>
      <template v-slot:description>
        <div v-if="user_form.first_name || user_form.last_name">{{ user_form.first_name + ' ' + user_form.last_name }}</div>
        <div v-else>{{ $t('profile.account.name.no_name') }}</div>
      </template>
      <template v-slot:content>
        <b-form-group
            class="mb-0"
        >
          <b-form-row>
            <b-col cols="12" sm="6">
              <!-- ### First name ################################################## -->
              <b-form-group
                  id="input-group-10"
                  :label="$t('signup.form.fname.label')"
                  label-for="input-10"
                  class="mb-0"
              >
                <b-form-input
                    id="input-10"
                    :disabled="loading"
                    v-model="$v.user_form.first_name.$model"
                    :placeholder="$t('signup.form.fname.ph')"
                    :state="validateUserFormState('first_name')"
                ></b-form-input>
                <b-form-invalid-feedback>{{
                    $t("form.required")
                  }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6">
              <!-- ### Last name ################################################## -->
              <b-form-group
                  id="input-group-11"
                  :label="$t('signup.form.lname.label')"
                  label-for="input-11"
                  class="mb-0"
              >
                <b-form-input
                    id="input-11"
                    :disabled="loading"
                    v-model="$v.user_form.last_name.$model"
                    :placeholder="$t('signup.form.lname.ph')"
                    :state="validateUserFormState('last_name')"
                ></b-form-input>
                <b-form-invalid-feedback>{{
                    $t("form.required")
                  }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-form-group>
      </template>
    </collapsible-card>


    <!-- Business or Individual ------------------------------------------------------------->
    <collapsible-card
        ref="type"
        :loading="loading || saveTypeRequest"
        :changed="isTypeDataChanged()"
        no-close-on-ok
        @click="handleSaveBusinessType()"
        @close="setTypeFields()"
    >
      <template v-slot:title>
        {{ $t('profile.account.type.title') }}
      </template>
      <template v-slot:description>
        <div class="mb-2">{{ $t('profile.account.registered_as')}} {{ type_form.is_individual ? $t("profile.account.as_individual") : $t("profile.account.as_business") }}</div>
        <template v-if="!type_form.is_individual">
          <div v-if="type_form.company_name">{{ $t('profile.account.type.company_name', [type_form.company_name]) }}</div>
          <div v-else>{{ $t('profile.account.type.no_name') }}</div>
          <div v-if="type_form.dba">{{ $t('profile.account.type.dba', [type_form.dba]) }}</div>
        </template>
      </template>
      <template v-slot:content>
        <b-form-group
            class="mb-0"
            :label="$t('signup.company')"
            label-size="lg"
            label-class="font-weight-bold pt-0"
        >
          <b-form-group :class="{ 'mb-0' : type_form.is_individual }">
            <b-form-checkbox
                v-model="type_form.is_individual"
                name="individual-check-button"
                switch
            >
              {{ $t("signup.form.individual") }}
            </b-form-checkbox>
          </b-form-group>

          <!-- ### Company name ################################################## -->
          <b-form-group
              v-if="!type_form.is_individual"
              id="input-group-1"
              :label="$t('signup.form.cname.label')"
              label-for="input-1"
          >
            <b-form-input
                id="input-1"
                autofocus
                :disabled="loading || type_form.is_individual"
                v-model="$v.type_form.company_name.$model"
                :placeholder="$t('signup.form.cname.ph')"
                :state="validateTypeFormState('company_name')"
            ></b-form-input>
            <b-form-invalid-feedback>{{
                $t("form.required")
              }}</b-form-invalid-feedback>
          </b-form-group>

          <!-- ### DBA ################################################## -->
          <b-form-group
              v-if="!type_form.is_individual"
              id="input-group-2"
              :label="$t('signup.form.dba.label')"
              label-class="optional"
              class="mb-0"
              label-for="input-2"
          >
            <b-form-input
                id="input-2"
                :disabled="loading || type_form.is_individual"
                v-model="$v.type_form.dba.$model"
                :placeholder="$t('signup.form.dba.ph')"
                :state="validateTypeFormState('dba')"
            ></b-form-input>
            <b-form-invalid-feedback>{{
                $t("form.required")
              }}</b-form-invalid-feedback>
          </b-form-group>
        </b-form-group>
      </template>
    </collapsible-card>

    <!-- Address ------------------------------------------------------------->
    <collapsible-card
        v-if="USER_ADDRESS"
        ref="address"
        :loading="loading || saveAddrRequest"
        :changed="isAddressDataChanged()"
        no-close-on-ok
        @click="handleSaveAddress()"
        @close="setAddressFields()"
    >
      <template v-slot:title>
        {{ $t('profile.account.address.title') }}
      </template>
      <template v-slot:description>
        <div v-if="address_form.address">{{ address_form.address }}</div>
        <div v-if="address_form.address2">{{ address_form.address2 }}</div>
        <template v-if="address_form.region">
          <div v-if="address_form.city">{{ address_form.region + ', ' + address_form.city }}</div>
          <div v-else>{{ address_form.region }}</div>
        </template>
        <template v-else>
          <div v-if="address_form.city">{{ address_form.city }}</div>
        </template>
        <div v-if="address_form.postal_code">{{ address_form.postal_code }}</div>
        <div v-if="address_form.country">{{ address_form.country }}</div>
      </template>
      <template v-slot:content>
        <!-- ### Address ################################################################################ -->
        <b-form-group
            class="mb-0"
        >
          <!-- ### Address 1 ################################################## -->
          <b-form-group
              id="input-group-3"
              :label="$t('signup.form.address.label')"
              label-for="input-3"
          >
            <b-form-input
                id="input-3"
                :disabled="loading"
                v-model="$v.address_form.address.$model"
                :placeholder="$t('signup.form.address.ph')"
                :state="validateAddressFormState('address')"
            ></b-form-input>
            <b-form-invalid-feedback>{{
                $t("form.required")
              }}</b-form-invalid-feedback>
          </b-form-group>

          <!-- ### Address 2 ################################################## -->
          <b-form-group
              id="input-group-4"
              :label="$t('signup.form.address2.label')"
              label-for="input-4"
          >
            <b-form-input
                id="input-4"
                :disabled="loading"
                v-model="$v.address_form.address2.$model"
                :placeholder="$t('signup.form.address2.ph')"
                :state="validateAddressFormState('address2')"
            ></b-form-input>
            <b-form-invalid-feedback>{{
                $t("form.required")
              }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-row>
            <b-col cols="12" sm="6">
              <!-- ### City ################################################## -->
              <b-form-group
                  id="input-group-5"
                  :label="$t('signup.form.city.label')"
                  label-for="input-5"
              >
                <b-form-input
                    id="input-5"
                    :disabled="loading"
                    v-model="$v.address_form.city.$model"
                    :placeholder="$t('signup.form.city.ph')"
                    :state="validateAddressFormState('city')"
                ></b-form-input>
                <b-form-invalid-feedback>{{
                    $t("form.required")
                  }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6">
              <!-- ### Region ################################################## -->
              <b-form-group
                  id="input-group-6"
                  :label="$t('signup.form.region.label')"
                  label-for="input-6"
              >
                <b-form-input
                    id="input-6"
                    :disabled="loading"
                    v-model="$v.address_form.region.$model"
                    :placeholder="$t('signup.form.region.ph')"
                    :state="validateAddressFormState('region')"
                ></b-form-input>
                <b-form-invalid-feedback>{{
                    $t("form.required")
                  }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-form-row>

          <b-form-row>
            <b-col cols="12" sm="6">
              <!-- ### Postal Code ################################################## -->
              <b-form-group
                  class="mb-sm-0"
                  id="input-group-7"
                  :label="$t('signup.form.zip.label')"
                  label-for="input-7"
              >
                <b-form-input
                    id="input-7"
                    :disabled="loading"
                    v-model="$v.address_form.postal_code.$model"
                    :placeholder="$t('signup.form.zip.ph')"
                    :state="validateAddressFormState('postal_code')"
                ></b-form-input>
                <b-form-invalid-feedback>{{
                    $t("form.required")
                  }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6">
              <!-- ### Country ################################################## -->
              <b-form-group
                  class="mb-sm-0"
                  id="input-group-8"
                  :label="$t('signup.form.country.label')"
                  label-for="input-8"
              >
                <b-form-select
                    id="input-8"
                    :disabled="loading || countriesLoading"
                    v-model="$v.address_form.country.$model"
                    :placeholder="$t('signup.form.country.ph')"
                    :state="validateAddressFormState('country')"
                    :options="$store.state.countries.items"
                >
                  <b-spinner small></b-spinner>
                </b-form-select>
                <b-form-invalid-feedback>{{
                    $t("form.required")
                  }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-form-group>
      </template>
    </collapsible-card>
  </content-container>
</template>

<script>
import { validationMixin } from "vuelidate";
import ContentContainer from "@/components/ContentContainer";
import CollapsibleCard from "@/components/CollapsibleCard";
import {LOAD_COUNTRIES, SETUP_FROM_DATA, UPDATE_CLIENT_DATA} from "@/store/consts";
import {
  URL_CHANGE_BUSINESS_TYPE,
  URL_CHANGE_CLIENT_ADDRESS, URL_CHANGE_USER_NAME,
  URL_LOAD_USER, USER_ADDRESS
} from "@/globals";


export default {
  name: "Account",
  mixins: [validationMixin],
  components: {
    ContentContainer,
    CollapsibleCard,
  },
  data: () => ({
    loading: false,
    saveAddrRequest: false,
    saveTypeRequest: false,
    saveNameRequest: false,
    user_form: {
      first_name: "",
      last_name: "",
    },
    type_form: {
      is_individual: false,
      company_name: "",
      dba: "",
    },
    address_form: {
      address: "",
      address2: "",
      city: "",
      region: "",
      postal_code: "",
      country: "",
    },
    countriesLoading: false,
  }),
  validations() {
    let result = {
      user_form: {},
      type_form: {},
      address_form: {},
    };
    if (!this.type_form.is_individual) {
      result.type_form.company_name = {}; //{ required };
    } else {
      result.type_form.company_name = {};
    }
    result.type_form.dba = {};

    result.user_form.first_name = {}; //{ required };
    result.user_form.last_name = {}; //{ required };

    result.address_form.address = {}; //{ required };
    result.address_form.address2 = {};
    result.address_form.city = {}; //{ required };
    result.address_form.region = {}; //{ required };
    result.address_form.postal_code = {}; //{ required };
    result.address_form.country = {}; //{ required };

    return result;
  },
  methods: {
    validateUserFormState(name) {
      const { $dirty, $error } = this.$v.user_form[name];
      return $dirty ? !$error : null;
    },
    validateTypeFormState(name) {
      const { $dirty, $error } = this.$v.type_form[name];
      return $dirty ? !$error : null;
    },
    validateAddressFormState(name) {
      const { $dirty, $error } = this.$v.address_form[name];
      return $dirty ? !$error : null;
    },
    isUserDataChanged() {
      // Валидация формы
      this.$v.user_form.$touch();
      if (this.$v.user_form.$anyError) {
        return false;
      }

      return this.user_form.first_name != this.$store.state.user.first_name ||
          this.user_form.last_name != this.$store.state.user.last_name;
    },
    isTypeDataChanged() {
      // Валидация формы
      this.$v.type_form.$touch();
      if (this.$v.type_form.$anyError) {
        return false;
      }

      return this.type_form.is_individual != this.$store.state.user.client.is_individual ||
          (!this.type_form.is_individual && (
              this.type_form.company_name != this.$store.state.user.client.company ||
              this.type_form.dba != this.$store.state.user.client.dba
          ));
    },
    isAddressDataChanged() {
      // Валидация формы
      this.$v.address_form.$touch();
      if (this.$v.address_form.$anyError) {
        return false;
      }

      return this.address_form.address != this.$store.state.user.client.address ||
          this.address_form.address2 != this.$store.state.user.client.address2 ||
          this.address_form.city != this.$store.state.user.client.city ||
          this.address_form.region != this.$store.state.user.client.region ||
          this.address_form.postal_code != this.$store.state.user.client.postal_code ||
          this.address_form.country != this.$store.state.user.client.country;
    },
    handleSaveBusinessType() {
      this.$patch(URL_CHANGE_BUSINESS_TYPE, {
        is_individual: this.type_form.is_individual,
        company: this.type_form.company_name,
        dba: this.type_form.dba
      }, true, 'saveTypeRequest', true)
          .then((result) => {
            // Обновим адрес клиента
            this.$store.dispatch(UPDATE_CLIENT_DATA, result);
            this.$refs.type.close();
          })
          .catch((e) => {
            this.$alert.show(e);
          });
    },
    handleSaveAddress() {
      this.$patch(URL_CHANGE_CLIENT_ADDRESS, {
            address: this.address_form.address,
            address2: this.address_form.address2,
            city: this.address_form.city,
            region: this.address_form.region,
            postal_code: this.address_form.postal_code,
            country: this.address_form.country
          }, true, 'saveAddrRequest', true)
          .then((result) => {
            // Обновим адрес клиента
            this.$store.dispatch(UPDATE_CLIENT_DATA, result);
            this.$refs.address.close();
          })
          .catch((e) => {
            this.$alert.show(e);
          });
    },
    handleSaveUserName() {
      this.$patch(URL_CHANGE_USER_NAME, {
        first_name: this.user_form.first_name,
        last_name: this.user_form.last_name
      }, true, 'saveNameRequest', true)
          .then(() => {
            // Обновим адрес клиента
            this.$store.commit('user_first_name', this.user_form.first_name);
            this.$store.commit('user_last_name', this.user_form.last_name);
            this.$refs.name.close();
          })
          .catch((e) => {
            this.$alert.show(e);
          });
    },
    setTypeFields() {
      this.type_form = {
        is_individual: this.$store.state.user.client.is_individual,
        company_name: this.$store.state.user.client.company,
        dba: this.$store.state.user.client.dba
      };
    },
    setUserFields() {
      this.user_form = {
        first_name: this.$store.state.user.first_name,
        last_name: this.$store.state.user.last_name
      };
    },
    setAddressFields() {
      this.address_form = {
        address: this.$store.state.user.client.address,
        address2: this.$store.state.user.client.address2,
        city: this.$store.state.user.client.city,
        region: this.$store.state.user.client.region,
        postal_code: this.$store.state.user.client.postal_code,
        country: this.$store.state.user.client.country,
      };
    }
  },
  mounted() {
    this.$store.dispatch(LOAD_COUNTRIES);
  },
  created() {
    this.USER_ADDRESS = USER_ADDRESS;

    // Токен должен быть свежим, перечитаем пользователя с сервера
    this.$get(URL_LOAD_USER, true)
      .then((result) => {
        // Обновим экземпляр пользователя
        this.$store.dispatch(SETUP_FROM_DATA, result);
        this.setTypeFields();
        this.setUserFields();
        this.setAddressFields();
      })
      .catch((e) => {
        // Странный случай, предложим войти в кабинет
        this.$router.push({ name: "sign-in" });
        this.$alert.show(e);
      });
  },
};
</script>

<style scoped>

</style>